import { create } from 'zustand';
import type {
  TranslationDirection,
  ConnectionStatus,
  CaptionState,
  TranscriptEntry,
  OBSStyleMode,
} from '../types';

// ==============================
// デフォルト用語集
// ==============================
const DEFAULT_GLOSSARY = [
  'ageLOC',
  'Prism iO',
  'LifePak Nano',
  'TRME',
  'Pharmanex',
  'System7',
  'Success Trip',
  'Ruby',
  'GSV',
  'PSV',
  'Brand Affiliate',
  'Executive Brand Partner',
];

// ==============================
// ストア型定義
// ==============================
interface SessionState {
  // 接続
  connectionStatus: ConnectionStatus;
  errorMessage: string | null;
  reconnectInfo: { attempt: number; delay: number } | null;

  // 翻訳
  direction: TranslationDirection;
  paused: boolean;
  isSpeaking: boolean;

  // 字幕
  currentCaption: CaptionState | null;

  // 設定
  fontSize: number;
  showOriginal: boolean;
  obsStyle: OBSStyleMode;
  captionPosition: 'bottom' | 'top' | 'center';
  sessionName: string;

  // 用語集
  glossaryTerms: string[];

  // 履歴
  transcript: TranscriptEntry[];

  // タイマー
  startTime: number | null;

  // アクション
  setConnectionStatus: (s: ConnectionStatus, error?: string) => void;
  setReconnectInfo: (info: { attempt: number; delay: number } | null) => void;
  setDirection: (d: TranslationDirection) => void;
  setPaused: (v: boolean) => void;
  setIsSpeaking: (v: boolean) => void;
  setCurrentCaption: (c: CaptionState | null) => void;
  clearCaption: () => void;
  setFontSize: (n: number) => void;
  setShowOriginal: (v: boolean) => void;
  setOBSStyle: (s: OBSStyleMode) => void;
  setCaptionPosition: (p: 'bottom' | 'top' | 'center') => void;
  setSessionName: (n: string) => void;
  initFromEvent: (glossary: string[]) => void;
  setGlossaryTerms: (terms: string[]) => void;
  addGlossaryTerm: (term: string) => void;
  removeGlossaryTerm: (term: string) => void;
  addTranscriptEntry: (entry: Omit<TranscriptEntry, 'id'>) => void;
  clearTranscript: () => void;
  exportTranscript: (format: 'txt' | 'csv' | 'json') => void;
  setStartTime: (t: number | null) => void;
  importGlossary: (terms: string[]) => void;
  exportGlossary: () => void;
}

export const useSessionStore = create<SessionState>((set, get) => ({
  // 初期値
  connectionStatus: 'idle',
  errorMessage: null,
  reconnectInfo: null,
  direction: 'en-ja',
  paused: false,
  isSpeaking: false,
  currentCaption: null,
  fontSize: 54,
  showOriginal: false,
  obsStyle: 'transparent-white',
  captionPosition: 'top',
  sessionName: '',
  glossaryTerms: [...DEFAULT_GLOSSARY],
  transcript: [],
  startTime: null,

  // ─────────────────────────────
  // アクション
  // ─────────────────────────────

  setConnectionStatus: (connectionStatus, error) =>
    set({ connectionStatus, errorMessage: error ?? null }),

  setReconnectInfo: (reconnectInfo) => set({ reconnectInfo }),

  setDirection: (direction) => set({ direction }),

  setPaused: (paused) => set({ paused }),

  setIsSpeaking: (isSpeaking) => set({ isSpeaking }),

  setCurrentCaption: (currentCaption) => set({ currentCaption }),

  clearCaption: () => set({ currentCaption: null }),

  setFontSize: (fontSize) => set({ fontSize: Math.max(40, Math.min(96, fontSize)) }),

  setShowOriginal: (showOriginal) => set({ showOriginal }),

  setOBSStyle: (obsStyle) => set({ obsStyle }),

  setCaptionPosition: (captionPosition) => set({ captionPosition }),

  setSessionName: (sessionName) => set({ sessionName }),

  setGlossaryTerms: (glossaryTerms) => set({ glossaryTerms }),

  // イベントの用語集で初期化（OperatorPage マウント時に呼ぶ）
  initFromEvent: (glossary: string[]) => {
    set({ glossaryTerms: glossary.length > 0 ? glossary : [...DEFAULT_GLOSSARY] });
  },

  addGlossaryTerm: (term) => {
    const { glossaryTerms } = get();
    const trimmed = term.trim();
    if (trimmed && !glossaryTerms.includes(trimmed)) {
      set({ glossaryTerms: [...glossaryTerms, trimmed] });
    }
  },

  removeGlossaryTerm: (term) =>
    set((state) => ({
      glossaryTerms: state.glossaryTerms.filter((t) => t !== term),
    })),

  addTranscriptEntry: (entry) =>
    set((state) => ({
      // 最大500件に制限
      transcript: [
        ...state.transcript.slice(-499),
        { ...entry, id: crypto.randomUUID() },
      ],
    })),

  clearTranscript: () => set({ transcript: [] }),

  exportTranscript: (format) => {
    const { transcript, sessionName } = get();
    if (transcript.length === 0) {
      alert('翻訳履歴がありません');
      return;
    }

    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const filename = `transcript-${sessionName || 'session'}-${timestamp}`;
    let content = '';

    if (format === 'json') {
      content = JSON.stringify(transcript, null, 2);
    } else if (format === 'csv') {
      const escape = (s: string) => `"${s.replace(/"/g, '""')}"`;
      content =
        'Time,Direction,Original,Translation\n' +
        transcript
          .map(
            (e) =>
              `${escape(new Date(e.timestamp).toLocaleTimeString('ja-JP'))},` +
              `${escape(e.direction)},` +
              `${escape(e.original)},` +
              `${escape(e.translation)}`
          )
          .join('\n');
    } else {
      // txt: 対訳形式
      content = transcript
        .map(
          (e) =>
            `[${new Date(e.timestamp).toLocaleTimeString('ja-JP')}] ${e.direction}\n` +
            `  原文: ${e.original}\n` +
            `  訳文: ${e.translation}`
        )
        .join('\n\n---\n\n');
    }

    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.${format}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  setStartTime: (startTime) => set({ startTime }),

  importGlossary: (terms) => {
    const unique = [...new Set(terms.map((t) => t.trim()).filter(Boolean))];
    set({ glossaryTerms: unique });
  },

  exportGlossary: () => {
    const { glossaryTerms } = get();
    const content = JSON.stringify({ terms: glossaryTerms }, null, 2);
    const blob = new Blob([content], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'glossary.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },
}));
